/**
 * Experience Card Image Swiper Component
 * Reusable image carousel/swiper for experience cards
 * Handles single and multiple images with swipe functionality
 */

import React, { useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { A11y, Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { borders } from '@/design-system/utils';
import { OptimizedImage } from '@/components/common/OptimizedImage';
import { cacheImages } from '@/services/imageCacheService';

export interface ExperienceCardImageSwiperProps {
  images: string[];
  title: string;
  swiperRef: React.MutableRefObject<SwiperType | null>;
  onTouchStart: () => void;
  onTouchMove: () => void;
  onTouchEnd: () => void;
  sizes?: string;
  className?: string;
}

export const ExperienceCardImageSwiper: React.FC<
  ExperienceCardImageSwiperProps
> = ({
  images,
  title,
  swiperRef,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  sizes = '(max-width: 768px) 100vw, 220px',
  className = 'experience-card-swiper',
}) => {
  const [imagesLoaded, setImagesLoaded] = React.useState<string[]>([]);
  const [isHovered, setIsHovered] = React.useState(false);

  // Defensive check: Filter out any non-string values to prevent "[object Object]" errors
  const validImages = React.useMemo(() => {
    if (!images || !Array.isArray(images)) return [];
    return images.filter(
      (img): img is string => typeof img === 'string' && img.length > 0
    );
  }, [images]);

  // Load only the first image initially
  useEffect(() => {
    if (validImages.length === 0) return;

    // Only preload the first image initially
    const firstImage = validImages[0];
    if (firstImage) {
      cacheImages([firstImage])
        .then(() => {
          setImagesLoaded([firstImage]);
        })
        .catch(() => {
          // Silently fail - still add to loaded list so it can render
          setImagesLoaded([firstImage]);
        });
    }
  }, [validImages]);

  // Load remaining images on hover
  const handleMouseEnter = () => {
    setIsHovered(true);

    if (validImages.length > 1 && imagesLoaded.length < validImages.length) {
      // Load remaining images
      const remainingImages = validImages.slice(1);
      cacheImages(remainingImages)
        .then(() => {
          setImagesLoaded(validImages);
        })
        .catch(() => {
          // Silently fail - still add to loaded list so they can render
          setImagesLoaded(validImages);
        });
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  // Only show images that are loaded (first image initially, rest on hover)
  const imagesToRender =
    imagesLoaded.length > 0 ? imagesLoaded : validImages.slice(0, 1); // Fallback to first image if not loaded yet

  if (validImages.length === 0) {
    return null;
  }

  // Single image - no need for Swiper
  if (validImages.length === 1) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <OptimizedImage
          src={validImages[0]}
          alt={title}
          fill
          sizes={sizes}
          style={{
            objectFit: 'cover',
          }}
          priority
          loading='eager'
        />
      </div>
    );
  }

  // Multiple images - use Swiper for swipe functionality
  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ width: '100%', height: '100%' }}
    >
      <Swiper
        onSwiper={swiper => {
          swiperRef.current = swiper;
          // Start autoplay stopped - will be controlled by hover
          if (swiper.autoplay && imagesToRender.length > 1) {
            swiper.autoplay.stop();
          }
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        slidesPerView={1}
        spaceBetween={0}
        modules={[A11y, Autoplay, Pagination]}
        speed={600}
        touchRatio={1}
        followFinger={true}
        threshold={15}
        shortSwipes={true}
        longSwipesRatio={0.5}
        longSwipesMs={300}
        touchAngle={30}
        simulateTouch={true}
        grabCursor={false}
        pagination={{
          clickable: false,
          dynamicBullets: false,
          bulletClass: 'swiper-pagination-bullet',
          bulletActiveClass: 'swiper-pagination-bullet-active',
        }}
        autoplay={
          imagesToRender.length > 1 && isHovered
            ? {
                delay: 1000,
                disableOnInteraction: false,
                pauseOnMouseEnter: false,
                stopOnLastSlide: false,
                waitForTransition: true,
              }
            : false
        }
        loop={imagesToRender.length > 1}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: borders.radius.lg,
        }}
        className={className}
        allowTouchMove={true}
        resistance={true}
        resistanceRatio={0.85}
        cssMode={false}
        watchSlidesProgress={true}
        touchStartPreventDefault={false}
        touchMoveStopPropagation={false}
        centeredSlides={true}
        slidesOffsetBefore={0}
        slidesOffsetAfter={0}
      >
        {imagesToRender.map((img, index) => (
          <SwiperSlide
            key={`${img}-${index}`}
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'relative',
                width: '100%',
                height: '100%',
              }}
            >
              <OptimizedImage
                src={img}
                alt={`${title} - Image ${index + 1}`}
                fill
                sizes={sizes}
                style={{
                  objectFit: 'cover',
                  pointerEvents: 'none',
                }}
                loading={index === 0 ? 'eager' : 'lazy'}
                priority={index === 0}
              />
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};
